import { Box } from '@material-ui/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import moment from 'moment';
import {connectStream} from '../../lib/bastetjs/utils/connectStream';
import eventBus from '../../lib/event-bus-rx';
import {EventHero} from '../../components/heroImage/EventHero';
import {NotFound} from '../notFound/NotFound';
import {PageTemplate} from '../home/PageTemplate';
import { OpenGraphTags } from '../../shared/OpenGraphTags';
import { StandardHeaderTags } from '../../shared/StandardHeaderTags';
import {EventResults} from '../eventResults/EventResults';
import {IndividualResult} from '../individualResult/IndividualResult';
import {EventRosterView} from '../eventRoster/EventRosterView';
import {EventAwards} from '../eventAwards/EventAwards';
import {getTokenRacerId} from '../../utils/isLoggedIn';
import { getResultsUrl } from '../../utils/resultsHelpers';
import { heroImageUrl, getImage } from '../../utils/heroImageUrl';
import {Loading} from '../../components/shared/Loading';
import { trackGoogleEvent } from '../../utils/googleEvents';
import { useGetEventCoursesQuery } from '../../api/alaskaApi';
import { theme } from '../../shared/theme';
import {
  getEventStatus,
  getHeroImage,
  checkHeroImage,
  setHeroImage,
} from '../../data/EventStreams';
import {
  fetchAthleteFollowing,
  getAthleteFollowingStream,
  getAthleteStream,
  fetchAthlete
} from '../../data/AthleteStreams';
import PropTypes from 'prop-types';
import { useStyles } from './styles';
import {EventAboutPage} from './EventAboutPage';
import { EventInfoModal } from '../../components/modal/EventInfoModal';
import { useLearnMoreOrRegisterButton } from '../../hooks/useLearnMoreOrRegisterButton';
import { useMasterEvent } from '../../hooks/useMasterEvent';
import { useIsEventLive } from '../../hooks/useIsEventLive';
import { getEventRaces } from '../../data/RaceStreams'
import { LTMobileAppPromo } from '../lt-mobile-app-promo/LTMobileAppPromo';

const eventsObj = {};

const messagesPerTab = (t) => ({
  about: {
    title: (name) => t('{{name}} on Athlinks', {name}),
    description: (name) =>
      t('View information and results about {{name}} on Athlinks', {name})
  },
  updates: {
    title: (name) => `${name} Updates`,
    description: (name) => `Get the latest updates for ${name}.`
  },
  roster: {
    title: (name) => t('{{name}} Roster', {name}),
    description: (name) => t('View the Roster for {{name}}', {name})},
  results: {
    title: (name, isCourseView, courseName) => isCourseView
      ? t('{{name}}: {{courseName}} Results', {name, courseName})
      : t('{{name}} Results Leaderboard',{name}),
    description: (name, isCourseView, courseName) => isCourseView
      ? t('View {{courseName}} results for {{name}}', {name, courseName})
      : t('View the race leaderboard for {{name}}', {name})
  },
  awards: {
    title: () => t('Awards'),
    description: () => t('View the awards for this event')
  }
});

const MasterLandingPageComponent = (props) => {
  const {
    masterId,
    tab,
    heroImage,
    selectedEventStatus,
    eventCoursesForCurrentRace,
  
    // ERP route params
    eventId,
    courseId,
    divisionId,
    search,
    friendsFilter,
  
    // IRP params
    entryId,
    bib,
    source,
  
    isMobile,
    eventCourses = { isLoading: true },
    following = {fetching: true, isLoading: true},
    athlete,
    referrerPath,
    mapPreview,

    history,
    location,

    isMasterEventFetching,
    masterEvent,
    masterEventError,
  } = props;

  const prevMasterId = useRef(masterId);
  const prevEventId = useRef(eventId);
  const prevAthlinksId = useRef(masterEvent?.currentRace?.athlinksId);

  const { t } = useTranslation();
  const styles = useStyles();

  const [heroCheckActive, setHeroCheckActive] = useState(false);

  const isEventLive = useIsEventLive(masterEvent?.currentRace.athlinksId || null);

  useEffect(() => {
    if (masterEvent) {
      const { currentRace, masterEventID } = masterEvent

      const nextAthlinksId = currentRace.athlinksId;
      const {affiliateRaceId, athlinksMasterId, eventType = '', hasHeroImage} = currentRace;
      //We check the hero image for existence so that we can properly add it to meta tags
      if (
        ((heroImage && heroImage.isLoading) || (masterId !== prevMasterId.current))
        && !heroCheckActive
        && affiliateRaceId
        && athlinksMasterId) {
          setHeroCheckActive(true);
          checkHeroImage(
            affiliateRaceId,
            athlinksMasterId,
            eventType.toLowerCase() || 'running',
            isMobile,
            hasHeroImage
          );
      } else if (((heroImage && heroImage.isLoading)
        || (masterId !== prevMasterId.current))
        && !affiliateRaceId
        && athlinksMasterId) {
        const size = isMobile ? 600 : 1600;
        const image = eventType
          ? getImage(eventType.toLowerCase(), size)
          : getImage('running', size)
        setHeroImage(athlinksMasterId, image);
      }

      if (heroImage && typeof(heroImage) === 'string') {
        setHeroCheckActive(false);
      }

      if (prevAthlinksId.current !== nextAthlinksId) {
        prevAthlinksId.current = nextAthlinksId;
        eventBus.publish('athlinksId', nextAthlinksId);
        eventsObj.next = nextAthlinksId;
      } else {
        eventsObj.next = -1;
      }

      if (prevEventId.current !== eventId) {
        getEventStatus('ERP-current-event', {eventId: eventId, masterId: masterEventID});
      }
    }

    prevMasterId.current = masterId;
    prevEventId.current = eventId;
  }, [masterId, heroImage, heroCheckActive, eventId, isMobile, masterEvent]);

  useEffect(() => {
    const racerId = getTokenRacerId();
    if (racerId) {
      fetchAthleteFollowing(racerId);
      fetchAthlete(racerId);
    }
  }, [masterId]);

  const getEventId = (isERP, override = false) => {
    const today = new Date();
    today.setHours(0,0,0,0);
    const todayTime = today.getTime();

    if (isMasterEventFetching || !masterEvent) {
      return 0;
    }

    const erpEventId = (masterEvent.eventRaces).reduce((acc, curr) => {
      const nextItemDate = new Date(curr.raceDate);
      nextItemDate.setHours(0,0,0,0);
      const nextItemDateTime = nextItemDate.getTime();
      const prevItemDate = new Date(acc.raceDate);
      prevItemDate.setHours(0,0,0,0);
      const prevItemDateTime = prevItemDate.getTime();

      if (acc.doesExist) {
        return acc;
      }
      else if (!override && curr.raceID === eventId) {
        return Object.assign({}, curr, {doesExist: true});
      }
      else if (isERP) {
        if (!acc.raceDate || (prevItemDate > nextItemDate && prevItemDateTime > todayTime)) {
          return curr;
        }
        else {
          return acc;
        }
      }
      else {
        if(nextItemDateTime >= todayTime) {
          return curr;
        }
        else if (acc.raceDate && new Date(acc.raceDate) > nextItemDate) {
          return acc;
        }
        else {
          return curr
        }
      }
    }, {});

    return erpEventId.raceID;
  };

  const getInitialRaceId = () => {
    const currentEvent = (masterEvent?.eventRaces || []).find((item) => item.raceID === getEventId(true, true));

    return currentEvent?.eventCourses?.[0]?.eventCourseID || -1;
  }

  const getRace = (eventCourseId, races) => {
    return races.races.filter((_) => _.eventCourseId === eventCourseId);
  }

  const extractRaceCourses = (masterEvent) => {
    if (masterEvent.eventRaces) {
      const eventCourses = masterEvent.eventRaces.map((race) => {
        return race.eventCourses.map((course) => {
          const epoch = moment.utc(race.raceDate).toDate().getTime();
          return {
            courseId: course.courseID,
            eventCourseId: course.eventCourseID,
            eventId: race.raceID,
            eventName: race.raceName,
            masterId: race.masterEventID,
            meters: course.distUnit,
            raceName: course.courseName,
            raceDate: race.raceDate,
            utcRaceDate: epoch,
            startEpoch: (epoch / 1000)
          }
        })
      });
      return {
        races: [].concat.apply([], eventCourses)
      }
    }
    else { return {races: []} }
  }

  const renderPage = (image) => {
    if (!masterEvent) {
      return null
    }

    if (tab === 'results') {
      trackGoogleEvent('ELP', 'Click', 'Results');
      //IRP
      if (entryId || bib) {
        return (
          <IndividualResult
            bib={bib}
            entryId={entryId}
            eventCourseId={courseId}
            eventId={eventId}
            image={image}
            isLive={isSelectedEventLive()}
            isMobile={isMobile}
            mapPreview={mapPreview}
            masterId={masterId}
            race={getRace(courseId, extractRaceCourses(masterEvent))}
            referrerPath={referrerPath}
            source={source}
          />
        )
      }
      const isEventLinked = !!masterEvent.eventRaces.find(x=> x.raceID === Number(eventCourses.SelectedRaceID))?.azpEventId
      //ERP
      return (
        <Box bgcolor={theme.palette.common.white}>
          <EventInfoModal
            actionButtons={location.state?.modalActionButtons}
            history={history}
            isMobile={isMobile}
            open={location.state?.showModal}
            title={location.state?.modalTitle}
            body={location.state?.modalBody}
          />
          <EventResults
            athlete={athlete}
            athleteId={getTokenRacerId()}
            courseId={courseId || 0}
            divisionId={divisionId || 0}
            eventCoursesDropDown={eventCourses.EventCoursesDropDown}
            eventId={getEventId(true)}
            following={following}
            friendsFilter={friendsFilter}
            isEventLive={isSelectedEventLive()}
            isEventLinked={isEventLinked}
            isMobile={isMobile}
            isSearch={!!search}
            masterId={masterId}
            races={extractRaceCourses(masterEvent)}
            search={search || ''}
            selectedCourseData={eventCourses.SelectedCourseData}
          />
        </Box>
      );
    }

    if (tab === 'roster') {
      return (
        <Box bgcolor={theme.palette.common.white}>
          <EventRosterView
            eventCourseId={courseId}
            masterId={masterId}
            isMobile={isMobile}
            athlete={athlete}
            races={masterEvent.eventRaces.filter((x) => !!x.eventCourses.length)}
            eventId={masterEvent.currentRace.athlinksId}
            divisionId={divisionId || 0}
            t={t}
          />
        </Box>
      );
    }

    if (tab === 'awards') {
      return (
        <Box bgcolor={theme.palette.common.white}>
          <EventAwards
            eventId={getEventId(true)}
            raceId={courseId}
            divisionId={divisionId}
            events={masterEvent.eventRaces}
            masterId={masterId}
            isMobile={isMobile}
            t={t}
          />
        </Box>
      )
    }

    return (
      <Box bgcolor={theme.palette.common.white}>
        <EventAboutPage
          masterEvent={masterEvent}
          isMobile={isMobile}
          eventsObj={eventsObj}
        />
      </Box>
    );
  }

  const { learnMoreRegisterButton } = useLearnMoreOrRegisterButton({
    ctLiveRegLink: eventCoursesForCurrentRace?.CTLiveRegistrationLink,
    eventId: masterEvent?.currentRace?.athlinksId,
    learnMoreLink: masterEvent?.currentRace?.siteUri,
    masterId,
    startTime: masterEvent?.currentRace?.startTime,
    thirdPartyRegLink: masterEvent?.currentRace?.thirdPartyRegLink
  })

  const isSelectedEventLive = () => {
    return (selectedEventStatus || {}).isLive;
  };

  const getErrorOverride = (error) => {
    if (error?.status === 404) {
      return {
        status: t('Event not found'),
        message: t('This event page does not exist.')
      };
    }

    return {status: '', message: ''};
  }

  if (isMasterEventFetching) {
    return <Loading centered={true} />;
  }

  console.log({ masterEvent, masterEventError })

  if (!masterEvent) {
    return (
      <NotFound
        noRedirect={true}
        errorStatus={404}
        errorOverride={getErrorOverride(masterEventError)}
      />
    )
  }

  const { currentRace } = masterEvent

  if (tab === 'roster' && !currentRace.isRosterAvailable) {
    return (<Redirect to={`/event/${masterId}`} />)
  }

  const liveEventURL = getResultsUrl(masterId, eventId);

  const selectedRace = masterEvent.eventRaces.find(({raceID}) => eventId === raceID) || currentRace || {};
  const isIRPView = !!(entryId || bib);
  const isCourseView = !!courseId && !isIRPView;

  const raceName = selectedRace && selectedRace.raceName
    ? selectedRace.raceName
    : selectedRace.name ? selectedRace.name : '';
  const selectedEventCourse = isCourseView && Array.isArray(selectedRace.eventCourses)
    ? selectedRace.eventCourses.find(({eventCourseID}) => courseId === eventCourseID) || {}
    : {};
  const messageSetKey = tab || 'about';
  const ogTitle = messagesPerTab(t)[messageSetKey].title(raceName, isCourseView, selectedEventCourse.courseName || '');
  const ogDescription = messagesPerTab(t)[messageSetKey].description(raceName, isCourseView, selectedEventCourse.courseName || '');
  const metaHeroImage = heroImage && heroImage.length
    ? heroImage
    : heroImageUrl(
      currentRace.eventType || 'Running',
      currentRace.isPublishedAthlinks && currentRace.hasHeroImage && currentRace.affiliateRaceId,
      true
    );
  const imageHeight = isMobile ? 338 : 900;
  const imageWidth = isMobile ? 600 : 1600;
  const image = {image: metaHeroImage, imageHeight, imageWidth};
  const erpEventId = getEventId(true, true);

  return (
    <PageTemplate
      unifiedSearchMode={true}
      headerIsFixed={true}
      isMobile={isMobile}
      paddingTop={0}
      isLive={isEventLive}
      isProfile={false}
      liveEventURL={liveEventURL}
      bannerTopOffset={50}
      t={t}
      {...props}
    >
      <StandardHeaderTags
        title={ogTitle}
        description={ogDescription}
      />
      <OpenGraphTags
        ogType='website'
        title={ogTitle}
        image={metaHeroImage}
        imageHeight={imageHeight}
        imageWidth={imageWidth}
        description={ogDescription}
      />
      <div
        itemScope
        key={masterId}
        itemType="http://schema.org/Event" className={styles.masterPageWrapper}
      >
        <div className={styles.masterPageContainer}>
          <EventHero
            actionButton={learnMoreRegisterButton}
            ctliveEventId={currentRace.affiliateRaceId}
            eventId={erpEventId}
            eventLogoUrl={currentRace.imageUrl}
            eventType={currentRace.eventType}
            isCustomHeroImage={currentRace.hasHeroImage && currentRace.isPublishedAthlinks}
            isLiveRaceHeaderVisible={isEventLive}
            isRosterTabAvailable={currentRace.isRosterAvailable}
            liveEventURL={liveEventURL}
            masterId={masterEvent.masterEventID}
            masterName={masterEvent.name}
            raceId={getInitialRaceId()}
          />
          {!isMasterEventFetching && renderPage(image)}
        </div>
      </div>
      <LTMobileAppPromo masterId={Number(masterId)} />
    </PageTemplate>
  );
};

const PureMasterLandingPageComponent = (props) => {
  const athlinksEventId = props.eventId
  const { data: eventCourses } = useGetEventCoursesQuery(athlinksEventId, {
    skip: !athlinksEventId
  })

  const { masterEvent, isMasterEventFetching, masterEventError } = useMasterEvent(props.masterId, athlinksEventId)

  const eventOptions = useMemo(() => {
    if (!masterEvent) {
      return []
    }
    
    return getEventRaces(masterEvent.eventRaces)
  }, [masterEvent])

  const currentRaceEventId = (eventOptions || []).find(option => option.isCurrentRace)?.id
  const { data: eventCoursesForCurrentRace } = useGetEventCoursesQuery(currentRaceEventId, {
    skip: !currentRaceEventId
  })

  return (
    <MasterLandingPageComponent
      {...props}
      eventCourses={eventCourses}
      eventCoursesForCurrentRace={eventCoursesForCurrentRace}
      isMasterEventFetching={isMasterEventFetching}
      masterEvent={masterEvent}
      masterEventError={masterEventError}
    />
  )
}

MasterLandingPageComponent.propTypes = {
  masterId: PropTypes.any.isRequired, //needed by connectStream to re-render page on changed masterId
  eventCourses: PropTypes.object,
  eventCoursesForCurrentRace: PropTypes.object,
  action: PropTypes.string,
  masterEvent: PropTypes.object,
  friendsFilter: PropTypes.string,
  t: PropTypes.func
};

export const MasterLandingPage = connectStream({
  following: () => getAthleteFollowingStream(getTokenRacerId()),
  selectedEventStatus: ({eventId, masterId}) => getEventStatus('ERP-current-event', {eventId, masterId}),
  athlete: () => getAthleteStream(getTokenRacerId()),
  heroImage: ({masterId}) => getHeroImage(masterId)
})(PureMasterLandingPageComponent);
